import { type ClassValue, clsx } from "clsx";
import { createTwMerge } from "fluid-tailwindcss/tailwind-merge";
import { mergeConfigs } from "tailwind-merge";

/**
 * `createTwMerge` from `fluid-tailwindcss/tailwind-merge` builds a tailwind-merge
 * instance preconfigured to recognise every `fl-*` utility (padding,
 * margin, gap, text, sizing, inset, border-radius, …) so merging works
 * correctly out of the box:
 *
 * - Fluid and standard utilities conflict at the same modifier level
 *   (e.g. `@md:fl-text-2xl/3xl` overrides `@md:text-5xl`).
 * - A container-prefixed fluid utility does NOT override its un-prefixed
 *   counterpart (e.g. `@md:fl-text-2xl/3xl` leaves `text-4xl` intact),
 *   matching the CSS cascade — the prefixed rule only applies inside the
 *   container query, so the base utility must survive as the mobile
 *   fallback.
 *
 * This replaces the previous hand-rolled adapter that was ported from
 * `fluid-tailwind` (the `~`-prefix variant). The codebase no longer uses
 * `~`-prefixed fluid classes — `fluid-tailwindcss` is the sole fluid plugin.
 */

/**
 * Plain tailwind-merge doesn't know our `text-xxs` size and treats it as a
 * text colour, so `text-xxs text-gray-400` would lose the size.
 */
const twMerge = createTwMerge((config: Parameters<typeof mergeConfigs>[0]) =>
  mergeConfigs(config, {
    extend: { classGroups: { "font-size": [{ text: ["xxs"] }] } },
  })
);

/**
 * Merge Tailwind CSS classes with clsx and tailwind-merge, with fluid utility
 * conflict resolution.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
